import FunnelHeader from '@/components/v1/FunnelHeader'
import FunnelInput from '@/components/v1/FunnelInput'
import FunnelLayout from '@/components/v1/FunnelLayout'
import StickyFooter from '@/components/v1/StickyFooter'
import { FUNNEL_PROGRESS_SECTIONS } from '@/pages/v1/onboarding/funnelSteps'
import AlreadySignedUpDialog from '@app-studio-web/ui/components/AlreadySignedUpDialog'
import ButtonSpinner from '@app-studio-web/ui/components/ButtonSpinner'
import { trackCompleteRegistrationEvent } from '@app-studio-web/ui/helpers/facebookTracking'
import {
  getPasswordFromSessionStorage,
  removePasswordFromSessionStorage,
} from '@app-studio-web/ui/helpers/sessionStorage'
import { signUp } from '@app-studio-web/ui/helpers/supabase'
import { validateEmail } from '@app-studio-web/ui/helpers/validations'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

/**
 * li-funnel-redesign v1 — the editorial email step. Keeps the shared page's
 * confirm-before-signup flow, supabase call and registration tracking.
 */
export default function EmailPage() {
  const [email, setEmail] = useState('')
  const [emailValid, setEmailValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState('')
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmailValid = validateEmail(email)

  const doSignUp = async () => {
    setIsSubmitting(true)

    const { error } = await signUp({ email, password })

    if (error) {
      if (error.message === 'User already registered') {
        setOpenAlreadySignedUpDialog(true)
      } else {
        console.error(error)
      }

      setIsSubmitting(false)
      setShowConfirm(false)

      return
    }

    removePasswordFromSessionStorage()

    await trackCompleteRegistrationEvent()

    navigate('/sign-up/preparing')
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    if (!isEmailValid) {
      setEmailValid(false)
      return
    }

    setEmailValid(true)
    setShowConfirm(true)
  }

  useEffect(() => {
    const passwordFromSessionStorage = getPasswordFromSessionStorage()

    if (passwordFromSessionStorage) {
      setPassword(passwordFromSessionStorage)
    } else {
      navigate('/sign-up/password')
    }
  }, [navigate])

  const header = (
    <FunnelHeader
      section="yourAccount"
      sectionProgress={1}
      sections={FUNNEL_PROGRESS_SECTIONS}
    />
  )

  if (showConfirm) {
    return (
      <FunnelLayout header={header}>
        <div className="flex grow flex-col gap-5 pt-1">
          <div className="animate-fade-up space-y-1.5">
            <h1 className="hw-heading text-left!">
              {t('components.emailPreSignupConfirm.title')}
            </h1>

            <p className="hw-subheading text-left!">
              {t('components.emailPreSignupConfirm.subtitle')}
            </p>
          </div>

          <p
            className="hw-card animate-fade-up text-hw-ink px-4 py-5 text-center text-lg font-bold break-all [animation-delay:120ms]"
            data-csr-mask
          >
            {email}
          </p>

          <StickyFooter>
            <div className="space-y-2">
              <button
                className="hw-button-primary"
                disabled={isSubmitting}
                onClick={doSignUp}
                type="button"
              >
                {isSubmitting && <ButtonSpinner />}

                {t('components.emailPreSignupConfirm.confirm')}
              </button>

              <button
                className="text-hw-muted w-full py-2 text-sm font-semibold transition-opacity hover:opacity-70 disabled:opacity-40"
                disabled={isSubmitting}
                onClick={() => setShowConfirm(false)}
                type="button"
              >
                {t('components.emailPreSignupConfirm.edit')}
              </button>
            </div>
          </StickyFooter>
        </div>

        <AlreadySignedUpDialog
          onClose={() => setOpenAlreadySignedUpDialog(false)}
          open={openAlreadySignedUpDialog}
        />
      </FunnelLayout>
    )
  }

  return (
    <FunnelLayout header={header}>
      <div className="flex grow flex-col gap-5 pt-1">
        <div className="animate-fade-up space-y-1.5">
          <h1 className="hw-heading text-left!">
            {t('pages.signUp.email.heading')}
          </h1>

          <p className="hw-subheading text-left!">
            {t('pages.signUp.email.subheading')}
          </p>
        </div>

        <form className="flex grow flex-col" noValidate onSubmit={handleSubmit}>
          <div className="animate-fade-up space-y-2 [animation-delay:120ms]">
            <FunnelInput
              autoComplete="email"
              autoFocus
              dataTestId="textfield-email"
              invalid={!emailValid}
              name="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t('shared.email')}
              type="email"
              value={email}
            />

            <p
              className={
                emailValid
                  ? 'text-hw-muted text-[13px] leading-relaxed'
                  : 'text-hw-red text-[13px] leading-relaxed'
              }
            >
              {emailValid ? (
                <Trans
                  components={{
                    privacyPolicyLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to="https://app-studio.ai/privacy"
                      />
                    ),
                    termsAndConditionsLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to="https://app-studio.ai/terms"
                      />
                    ),
                  }}
                  i18nKey="pages.signUp.email.text"
                />
              ) : (
                t('pages.signUp.email.emailNotValid')
              )}
            </p>
          </div>

          <StickyFooter>
            <button
              className="hw-button-primary"
              data-testid="button-continue-credentials"
              disabled={email.length < 1 || isSubmitting}
              type="submit"
            >
              {isSubmitting && <ButtonSpinner />}

              {t('shared.continue')}
            </button>
          </StickyFooter>
        </form>
      </div>

      <AlreadySignedUpDialog
        onClose={() => setOpenAlreadySignedUpDialog(false)}
        open={openAlreadySignedUpDialog}
      />
    </FunnelLayout>
  )
}
